/**
 * Registro de guías de ayuda in-app (AUTONOMY · Layer 3).
 *
 * Manifiesto central (patrón CatalogSource): las guías se registran aquí y un
 * test de completitud (`__tests__/registry.test.ts`) asegura que:
 *  - keys/slugs/section-ids son únicos y no vacíos,
 *  - TODA área navegable de la app (`AREA_HELP`) apunta a una guía+sección
 *    existentes, y
 *  - toda ruta de feature bajo `routes/_app` pertenece a un área con ayuda.
 *
 * Añadir una feature nueva ⇒ o se añade su área aquí (con guía/sección) o el
 * test falla: ninguna pantalla sin documentación.
 */
import { adminGuide } from "./guides/admin-guide";
import { teacherGuide } from "./guides/teacher-guide";
import { financeGuide } from "./guides/finance-guide";
import { crmGuide } from "./guides/crm-guide";
import { studentGuide } from "./guides/student-guide";
import { ioGuide } from "./guides/io-guide";
import { integrationsGuide } from "./guides/integrations-guide";
import { aiTutorGuide } from "./guides/ai-tutor-guide";
// B8 · autoría con IA + asistente de la academia.
import { aiAuthoringGuide } from "./guides/ai-authoring-guide";
import type { HelpGuide, HelpTarget } from "./types";

/** Todas las guías, en orden de presentación en `/help`. */
export const HELP_GUIDES: HelpGuide[] = [
  adminGuide,
  teacherGuide,
  financeGuide,
  crmGuide,
  studentGuide,
  ioGuide,
  integrationsGuide,
  aiTutorGuide,
  aiAuthoringGuide,
];

export function findGuide(slug: string): HelpGuide | undefined {
  return HELP_GUIDES.find((g) => g.slug === slug);
}

/**
 * Mapa área-de-la-app → sección de ayuda relevante. La clave es el primer
 * segmento de la ruta bajo `/u/$u` (p.ej. `academy`, `finance`). Lo consumen
 * el botón contextual «?» (`HelpButton`) y el test de completitud.
 */
export const AREA_HELP: Readonly<Record<string, HelpTarget>> = {
  learn: { guide: "alumno", section: "aprender" },
  catalog: { guide: "alumno", section: "catalogo" },
  leaderboard: { guide: "alumno", section: "aprender" },
  certificates: { guide: "alumno", section: "certificados" },
  schedule: { guide: "alumno", section: "horario" },
  // 1C/1D: el aula del alumno y su expediente.
  classroom: { guide: "alumno", section: "aula" },
  checkin: { guide: "alumno", section: "horario" },
  assignments: { guide: "alumno", section: "tareas" },
  "final-project": { guide: "alumno", section: "proyecto-final" },
  // B6 · niveles: el camino del alumno.
  path: { guide: "alumno", section: "mi-ruta" },
  payments: { guide: "alumno", section: "pagos" },
  notifications: { guide: "alumno", section: "comunicaciones" },
  invitations: { guide: "alumno", section: "personalizacion" },
  profile: { guide: "alumno", section: "personalizacion" },
  // §M1: «Mis perfiles» — qué soy en cada academia y qué desbloquea cada perfil.
  profiles: { guide: "alumno", section: "mis-perfiles" },
  themes: { guide: "alumno", section: "personalizacion" },
  language: { guide: "alumno", section: "personalizacion" },
  live: { guide: "profesor", section: "clases-en-vivo" },
  authoring: { guide: "administrador", section: "academico" },
  academy: { guide: "administrador", section: "academico" },
  organizations: { guide: "administrador", section: "configuracion" },
  activity: { guide: "administrador", section: "trazabilidad" },
  trash: { guide: "administrador", section: "trazabilidad" },
  finance: { guide: "caja", section: "registrar-pagos" },
  crm: { guide: "captacion", section: "pipeline" },
  "ai-tutor": { guide: "tutor-ia", section: "chat" },
  admin: { guide: "administrador", section: "features" },
  help: { guide: "administrador", section: "features" },
  // Sub-áreas con guía propia (el «?» de esas pantallas apunta más fino).
  "admin.import-export": { guide: "importar", section: "panorama" },
  "admin.integrations": { guide: "integraciones", section: "panorama" },
  // B8 · el panel del asistente y la autoría con IA tienen guía propia.
  "admin.academy-bot": { guide: "autoria-ia", section: "asistente" },
  "admin.billing": { guide: "caja", section: "configuracion" },
  "academy.cohorts": { guide: "administrador", section: "academico" },
  "academy.tutoring": { guide: "profesor", section: "tutorias" },
  // B1: la política de impago es una decisión de CAJA (quien cobra la decide).
  "academy.entitlement": { guide: "caja", section: "titularidad" },
  "academy.campuses": { guide: "administrador", section: "modalidad" },
  "finance.rates": { guide: "caja", section: "tasa" },
};

/** Resuelve el target de ayuda de un área (undefined si no está mapeada). */
export function helpTargetForArea(area: string): HelpTarget | undefined {
  return AREA_HELP[area];
}

/**
 * §M2 · Resumen de una sección (el «texto explicativo» de una pantalla).
 * Es la MISMA prosa que se ve en la guía: la pantalla no reescribe su propia
 * explicación, la toma de aquí.
 */
export function helpSummaryFor(target: HelpTarget) {
  return findGuide(target.guide)?.sections.find((s) => s.id === target.section)?.summary;
}

/**
 * Validación anti-deriva del registro (espejo de `FeatureCatalog::validate`).
 * Devuelve la lista de problemas; vacía = registro sano.
 *
 * Los mensajes son diagnósticos PARA DESARROLLADORES (fallan el test de
 * completitud) — nunca llegan a la UI, por eso no se i18n-izan.
 */
/* eslint-disable lingui/no-unlocalized-strings */
export function validateHelpRegistry(): string[] {
  const issues: string[] = [];
  const slugs = new Set<string>();
  const keys = new Set<string>();
  for (const guide of HELP_GUIDES) {
    if (!/^help\.[a-z_]+$/.test(guide.key)) {
      issues.push(`guide key inválida: ${guide.key}`);
    }
    if (keys.has(guide.key)) {
      issues.push(`guide key duplicada: ${guide.key}`);
    }
    keys.add(guide.key);
    if (!/^[a-z][a-z-]*$/.test(guide.slug)) {
      issues.push(`guide slug inválido: ${guide.slug}`);
    }
    if (slugs.has(guide.slug)) {
      issues.push(`guide slug duplicado: ${guide.slug}`);
    }
    slugs.add(guide.slug);
    if (guide.sections.length === 0) {
      issues.push(`guía sin secciones: ${guide.key}`);
    }
    const sectionIds = new Set<string>();
    for (const section of guide.sections) {
      if (!/^[a-z][a-z-]*$/.test(section.id)) {
        issues.push(`section id inválido: ${guide.key}#${section.id}`);
      }
      if (sectionIds.has(section.id)) {
        issues.push(`section id duplicado: ${guide.key}#${section.id}`);
      }
      sectionIds.add(section.id);
    }
  }
  for (const [area, target] of Object.entries(AREA_HELP)) {
    const guide = findGuide(target.guide);
    if (!guide) {
      issues.push(`área ${area}: guía inexistente ${target.guide}`);
    } else if (!guide.sections.some((s) => s.id === target.section)) {
      issues.push(`área ${area}: sección inexistente ${target.guide}#${target.section}`);
    }
  }
  return issues;
}
/* eslint-enable lingui/no-unlocalized-strings */
